import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { FaArrowLeft, FaPaperPlane, FaPaperclip, FaStar } from 'react-icons/fa';
import UserActionsMenu from '@/components/UserActionsMenu/UserActionsMenu';
import { fetchThreadMessages, sendMessage as sendMessageThunk, markMessagesAsRead } from '@/redux/thunks/messagesThunks';
import { addOptimisticMessage, updateMessageReadStatus } from '@/redux/slices/messagesSlice';
import { useSocket } from '@/contexts/SocketContext';
import { LoadingSpinner } from '@/components';
import { getMediaUrl } from '@/constants/media';
import { formatHandle } from '@/utils/formatName';
import MessageBubble from './MessageBubble';
import { UnifiedSpotlightModal } from '@/components/UnifiedSpotlightModal';
import { useDMSpotlight } from '@/hooks/useDMSpotlight';
import { toast } from 'react-toastify';
import './ChatArea.scss';

interface ChatAreaProps {
  threadId: string | null;
  onBack?: () => void; // For mobile back navigation
  onThreadDeleted?: () => void; // Parent clears its selected thread so the chat unmounts
}

const ChatArea: React.FC<ChatAreaProps> = ({ threadId, onBack, onThreadDeleted }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth?.user);
  const messages = useAppSelector((state) => state.messages.activeThread.messages);
  const isLoading = useAppSelector((state) => state.messages.loading.messages);
  const threads = useAppSelector((state) => state.messages.threads);
  const { socket, joinThread, leaveThread, sendTyping, sendStopTyping, markThreadAsRead } = useSocket();

  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [spotlightEnabled, setSpotlightEnabled] = useState(false);
  const [showSpotlightModal, setShowSpotlightModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    spotlightFee,
    prepareTransaction,
    executeTransaction,
    sendSpotlightMessage,
    isLoading: isSpotlightLoading,
    reset: resetSpotlight,
  } = useDMSpotlight();

  const user = useAppSelector((state) => state.auth.user);
  const isEmailUser = user?.authMethod === 'email';

  // Get participant info from thread
  const thread = threads.find((t) => t.id === threadId);
  const otherParticipant = thread?.participants.find(p => p.id !== currentUser?.id) || {
    id: '',
    name: 'User Name',
    username: 'username',
    avatar: null,
    isBot: false,
  };

  // Fetch messages and join socket room when thread changes
  useEffect(() => {
    if (threadId) {
      dispatch(fetchThreadMessages(threadId));
      // Join socket room for real-time updates
      joinThread(threadId);
      // Mark thread as read on the server. The socket emit only persists the
      // DB rows + broadcasts to OTHER participants; it doesn't update local
      // Redux state. The HTTP thunk's fulfilled reducer zeroes
      // thread.unreadCount which the AppBar badge reads from, so the badge
      // clears without waiting for a page refresh.
      markThreadAsRead(threadId);
      dispatch(markMessagesAsRead(threadId));
    }

    // Reset spotlight state when thread changes
    setSpotlightEnabled(false);
    setShowSpotlightModal(false);
    resetSpotlight();

    // Leave room when component unmounts or thread changes
    return () => {
      if (threadId) {
        leaveThread(threadId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, dispatch, joinThread, leaveThread, markThreadAsRead]);

  // Socket event listeners
  useEffect(() => {
    if (!socket || !threadId) return;

    // Note: message-received is handled globally in MessagesPage to avoid duplicates
    // We only handle typing indicators and read receipts here

    // Listen for typing indicators
    const handleUserTyping = (data: { threadId: string; username: string; userId: string }) => {
      if (data.threadId === threadId && data.userId !== currentUser?.id) {
        setIsTyping(true);

        // Auto-hide typing indicator after 3 seconds
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      }
    };

    const handleUserStoppedTyping = (data: { threadId: string; userId: string }) => {
      if (data.threadId === threadId && data.userId !== currentUser?.id) {
        setIsTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
    };

    // Listen for read receipts
    const handleMessageReadUpdate = (data: { messageId: string; threadId: string; readBy: string }) => {
      if (data.threadId === threadId && data.readBy !== currentUser?.id) {
        dispatch(updateMessageReadStatus({ messageId: data.messageId, read: true }));
      }
    };

    const handleThreadReadUpdate = (data: { threadId: string; readBy: string }) => {
      if (data.threadId === threadId && data.readBy !== currentUser?.id) {
        // Mark all messages from current user as read
        messages.forEach(msg => {
          if (msg.senderId === currentUser?.id) {
            dispatch(updateMessageReadStatus({ messageId: msg.id, read: true }));
          }
        });
      }
    };

    socket.on('user-typing', handleUserTyping);
    socket.on('user-stopped-typing', handleUserStoppedTyping);
    socket.on('message-read-update', handleMessageReadUpdate);
    socket.on('thread-read-update', handleThreadReadUpdate);

    return () => {
      socket.off('user-typing', handleUserTyping);
      socket.off('user-stopped-typing', handleUserStoppedTyping);
      socket.off('message-read-update', handleMessageReadUpdate);
      socket.off('thread-read-update', handleThreadReadUpdate);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [socket, threadId, dispatch, currentUser?.id, messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle spotlight toggle click
  const handleSpotlightToggle = () => {
    if (!spotlightEnabled) {
      // Enabling spotlight - check if we have a message to send
      if (messageInput.trim()) {
        setPendingMessage(messageInput.trim());
        setSpotlightEnabled(true);
        setShowSpotlightModal(true);
      } else {
        toast.info('Please type a message first');
      }
    } else {
      // Disabling spotlight
      setSpotlightEnabled(false);
      resetSpotlight();
    }
  };

  // Prepare transaction and fetch fee when modal opens
  const handleFetchFee = async (): Promise<{ price: number; canProceed?: boolean; warning?: string; balance?: { current: number; required: string; shortfall: string | null } }> => {
    if (!otherParticipant.id) {
      throw new Error('Recipient not found');
    }

    try {
      const result = await prepareTransaction({ recipientId: otherParticipant.id });
      return result;
    } catch (err: any) {
      toast.error(err.message || 'Failed to load spotlight fee');
      throw err;
    }
  };

  // Handle spotlight confirmation (duration parameter not used for DM)
  const handleConfirmSpotlight = async (durationHours: number) => {
    void durationHours;
    if (!threadId || !pendingMessage) return;

    setIsSending(true);

    try {
      // Stop typing indicator immediately when sending
      sendStopTyping(threadId);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      let txHash: string | undefined;

      // Wallet users: sign transaction
      if (!isEmailUser) {
        toast.info('Please sign the transaction in your Klever wallet...');
        txHash = await executeTransaction();
      } else {
        toast.info('Processing spotlight...');
      }

      // Optimistically add message to UI
      const tempId = `temp-${Date.now()}`;
      const tempMessage = {
        id: tempId,
        content: pendingMessage,
        senderId: currentUser?.id || '',
        timestamp: new Date().toISOString(),
        threadId,
        isSpotlighted: true,
      };
      dispatch(addOptimisticMessage(tempMessage));

      // Send spotlight message (with txHash for wallet users)
      await sendSpotlightMessage(
        {
          threadId,
          content: pendingMessage,
        },
        txHash
      );

      toast.success('Spotlight message sent!');

      // Clear input and reset
      setMessageInput('');
      setPendingMessage('');
      setSpotlightEnabled(false);
      setShowSpotlightModal(false);
      resetSpotlight();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send spotlight message');
      console.error('Failed to send spotlight message:', error);
      setMessageInput(pendingMessage); // Restore message on failure
    } finally {
      setIsSending(false);
    }
  };

  // Handle spotlight cancel
  const handleCancelSpotlight = () => {
    setShowSpotlightModal(false);
    setPendingMessage('');
    setSpotlightEnabled(false);
    resetSpotlight();
  };

  // Regular message send (non-spotlight)
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageInput.trim() || !threadId || isSending) return;

    const content = messageInput.trim();

    // Check if spotlight is enabled
    if (spotlightEnabled) {
      setPendingMessage(content);
      setShowSpotlightModal(true);
      return;
    }

    // Regular message send
    setMessageInput('');
    setIsSending(true);

    // Stop typing indicator immediately when sending
    sendStopTyping(threadId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Optimistically add message to UI
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      content,
      senderId: currentUser?.id || '',
      timestamp: new Date().toISOString(),
      threadId,
      isSpotlighted: false,
    };
    dispatch(addOptimisticMessage(tempMessage));

    try {
      await dispatch(sendMessageThunk({
        threadId,
        content,
      })).unwrap();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(error?.message || 'Failed to send message');
      setMessageInput(content); // Restore message on failure
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };

  // Handle input change and send typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);

    // Send typing indicator if thread exists
    if (threadId && e.target.value.trim()) {
      sendTyping(threadId);

      // Auto-send stop typing after 2 seconds of inactivity
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        if (threadId) {
          sendStopTyping(threadId);
        }
      }, 2000);
    } else if (threadId) {
      // If input is empty, stop typing
      sendStopTyping(threadId);
    }
  };

  // Empty state when no thread selected
  if (!threadId) {
    return (
      <div className="chat-area chat-empty">
        <div className="empty-state">
          <div className="empty-icon">💬</div>
          <h3>Select a conversation</h3>
          <p>Choose a conversation from the list to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          {onBack && (
            <button className="btn-back" onClick={onBack}>
              <FaArrowLeft />
            </button>
          )}
          <div
            className="participant-avatar clickable"
            onClick={() => navigate(`/profile/${otherParticipant.username}`)}
            title={`View ${otherParticipant.name}'s profile`}
          >
            {otherParticipant.avatar ? (
              <img src={getMediaUrl(otherParticipant.avatar) || undefined} alt={otherParticipant.name} />
            ) : (
              <div className="avatar-placeholder">
                {otherParticipant.name[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="participant-info">
            <h3 className="participant-name">{otherParticipant.name}</h3>
            <span className="participant-username">{formatHandle(otherParticipant.username)}</span>
          </div>
        </div>
        <UserActionsMenu
          targetUserId={otherParticipant.id}
          targetName={otherParticipant.name}
          threadId={threadId ?? undefined}
          onThreadDeleted={onThreadDeleted ?? onBack}
        />
      </div>

      {/* Messages Area */}
      <div className="chat-messages">
        {isLoading ? (
          <LoadingSpinner size="medium" message="Loading messages..." />
        ) : messages.length === 0 ? (
          <div className="messages-empty">
            <p>No messages yet. Send the first message!</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isOwn = message.senderId === currentUser?.id;
              return (
                <MessageBubble
                  key={message.id || `message-${index}`}
                  content={message.content}
                  timestamp={message.timestamp}
                  isOwn={isOwn}
                  senderName={isOwn ? 'You' : otherParticipant.name}
                  senderAvatar={isOwn ? undefined : otherParticipant.avatar}
                  senderUsername={isOwn ? undefined : otherParticipant.username}
                  senderIsBot={isOwn ? false : !!otherParticipant.isBot}
                  read={message.read}
                  isSpotlighted={message.isSpotlighted}
                />
              );
            })}
            {isTyping && (
              <div className="typing-indicator">
                <div className="typing-avatar">
                  {otherParticipant.avatar ? (
                    <img src={getMediaUrl(otherParticipant.avatar) || undefined} alt={otherParticipant.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {otherParticipant.name[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="typing-bubble">
                  <span className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className="chat-input-container">
        <form onSubmit={handleSendMessage} className="chat-input-form">
          <button type="button" className="btn-attach" disabled>
            <FaPaperclip />
          </button>
          <textarea
            className="message-input"
            placeholder="Type a message..."
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={() => setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 300)}
            rows={1}
            disabled={isSending}
          />
          <button
            type="button"
            className={`btn-spotlight ${spotlightEnabled ? 'active' : ''}`}
            onClick={handleSpotlightToggle}
            disabled={isSending}
            title={spotlightEnabled && spotlightFee ? `Spotlight (${parseFloat(spotlightFee).toFixed(2)} KLV)` : 'Spotlight DM'}
          >
            <FaStar />
          </button>
          <button
            type="submit"
            className="btn-send"
            disabled={!messageInput.trim() || isSending}
          >
            <FaPaperPlane />
          </button>
        </form>
      </div>

      {/* Unified Spotlight Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <UnifiedSpotlightModal
          isOpen={showSpotlightModal}
          title="Spotlight DM"
          onConfirm={handleConfirmSpotlight}
          onCancel={handleCancelSpotlight}
          onDurationChange={handleFetchFee}
          isProcessing={isSending || isSpotlightLoading}
          defaultDuration={1}
          showDurationSelector={false}
          description={`You are about to send a spotlighted message to ${otherParticipant.name}. Spotlighted messages appear with special visual effects and stand out in the recipient's inbox.`}
          explainer={`This is private — only ${otherParticipant.name} sees the spotlighted message. It stands out in their inbox with a sparkle effect but isn't visible to anyone else on the platform.`}
        />,
        document.body
      )}
    </div>
  );
};

export default ChatArea;
